/**
 * Code-snippet generators for embedding a saved style in third-party apps.
 * The single `styleUrl` already contains the `apikey` query param, so
 * consumers just paste the snippet and they're done.
 */

export type SnippetKind = "iframe" | "gljs" | "npm" | "flutter" | "rn" | "android" | "ios" | "curl";

export type Snippet = {
  kind: SnippetKind;
  label: string;
  language: "html" | "javascript" | "dart" | "tsx" | "kotlin" | "swift" | "bash";
  code: string;
};

export const GL_JS_PACKAGE = "@vietmap/vietmap-gl-js";
export const GL_JS_NPM_URL = `https://www.npmjs.com/package/${GL_JS_PACKAGE}`;
/** Used until (or if) the npm registry lookup below answers. */
export const GL_JS_FALLBACK_VERSION = "6.0.1";

let latestStable: Promise<string> | null = null;

/**
 * Newest stable `@vietmap/vietmap-gl-js` version from the npm registry
 * (CORS-enabled). The `latest` dist-tag can point at a pre-release
 * (e.g. 7.0.0-pre.1), so pick the highest version without a pre-release
 * suffix. Cached for the session; falls back to GL_JS_FALLBACK_VERSION.
 */
export function fetchLatestGlJsVersion(): Promise<string> {
  latestStable ??= fetch(`https://registry.npmjs.org/${GL_JS_PACKAGE}`, {
    headers: { Accept: "application/vnd.npm.install-v1+json" },
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
    .then((doc: { versions?: Record<string, unknown> }) => {
      const stable = Object.keys(doc.versions ?? {}).filter((v) => /^\d+\.\d+\.\d+$/.test(v));
      stable.sort((a, b) => {
        const pa = a.split(".").map(Number);
        const pb = b.split(".").map(Number);
        return pa[0] - pb[0] || pa[1] - pb[1] || pa[2] - pb[2];
      });
      return stable.pop() ?? GL_JS_FALLBACK_VERSION;
    })
    .catch(() => GL_JS_FALLBACK_VERSION);
  return latestStable;
}

/** `iframeHtml`: ready `<iframe>` of the public embed page (saved styles only). */
export function buildSnippets(styleUrl: string, glJsVersion = GL_JS_FALLBACK_VERSION, iframeHtml?: string): Snippet[] {
  const cdn = `https://unpkg.com/${GL_JS_PACKAGE}@${glJsVersion}/dist`;
  return [
    ...(iframeHtml ? [{ kind: "iframe" as const, label: "Iframe", language: "html" as const, code: iframeHtml }] : []),
    {
      kind: "gljs",
      label: "Web (HTML)",
      language: "html",
      code: `<link  href="${cdn}/vietmap-gl.css" rel="stylesheet" />
<script src="${cdn}/vietmap-gl.js"></script>

<div id="map" style="width:100%;height:480px"></div>
<script>
  const map = new vietmapgl.Map({
    container: "map",
    style: ${JSON.stringify(styleUrl)},
    center: [108, 14.5], // [lng, lat]
    zoom: 5,
  });
  map.addControl(new vietmapgl.NavigationControl());
</script>`,
    },
    {
      kind: "npm",
      label: "Web (npm)",
      language: "javascript",
      code: `// npm i ${GL_JS_PACKAGE}@${glJsVersion}
// The package has no "main" entry — import from dist/.
import vietmapgl from "${GL_JS_PACKAGE}/dist/vietmap-gl";
import "${GL_JS_PACKAGE}/dist/vietmap-gl.css";

const map = new vietmapgl.Map({
  container: "map", // element with a fixed height
  style: ${JSON.stringify(styleUrl)},
  center: [108, 14.5], // [lng, lat]
  zoom: 5,
});
map.addControl(new vietmapgl.NavigationControl());`,
    },
    {
      kind: "flutter",
      label: "Flutter",
      language: "dart",
      code: `// pubspec.yaml: vietmap_flutter_gl: latest
import 'package:vietmap_flutter_gl/vietmap_flutter_gl.dart';

VietmapGL(
  styleString: ${JSON.stringify(styleUrl)},
  initialCameraPosition: const CameraPosition(
    target: LatLng(14.5, 108),
    zoom: 5,
  ),
);`,
    },
    {
      kind: "rn",
      label: "React Native",
      language: "tsx",
      code: `// npm i @vietmap/vietmap-gl-react-native
import { MapView, Camera } from "@vietmap/vietmap-gl-react-native";

<MapView style={{ flex: 1 }} mapStyle=${JSON.stringify(styleUrl)}>
  <Camera zoomLevel={5} centerCoordinate={[108, 14.5]} />
</MapView>`,
    },
    {
      kind: "android",
      label: "Android (Kotlin)",
      language: "kotlin",
      code: `// implementation 'com.github.vietmap-company:maps-sdk-android:2.0.4'
Vietmap.getInstance(context)

mapView.getMapAsync { vietMapGL ->
  vietMapGL.setStyle(
    Style.Builder().fromUri(${JSON.stringify(styleUrl)})
  )
}`,
    },
    {
      kind: "ios",
      label: "iOS (Swift)",
      language: "swift",
      code: `// pod 'Vietmap', '~> 1.2.2'
import Vietmap

let url = URL(string: ${JSON.stringify(styleUrl)})!
let mapView = MGLMapView(frame: view.bounds, styleURL: url)
mapView.setCenter(CLLocationCoordinate2D(latitude: 14.5, longitude: 108), zoomLevel: 5, animated: false)
view.addSubview(mapView)`,
    },
    {
      kind: "curl",
      label: "cURL",
      language: "bash",
      code: `curl -L ${JSON.stringify(styleUrl)} | jq .`,
    },
  ];
}
